import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';

import { useParams } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import DegreeDescriptionEoiStateContext from 'bundles/degree-description/contexts/DegreeDescriptionEoiStateContext';
import { useDegreeDescriptionPageData } from 'bundles/degree-description/contexts/DegreeDescriptionPageContext';
import { getQualifyingQuestionsExpt } from 'bundles/degree-description/utils/experiments';
import type { SectionName } from 'bundles/expression-of-interest/components/EoiDialog';
import { isLearnerQualified } from 'bundles/expression-of-interest/components/experiments/qualifyingQuestionsUtils';
import { ACTION_TYPES } from 'bundles/expression-of-interest/constants';
import { getInputfromUserProfile, isDestinationDegree } from 'bundles/expression-of-interest/utils/util';

export const DegreeDescriptionEoiStateProvider: React.LegacyFunctionComponentWithChildren = ({ children }) => {
  const { productVariant, eoiSurveyData, pageContent } = useDegreeDescriptionPageData();
  const [hasCompletedEoi, setHasCompletedEoi] = useState<boolean>(false);

  const hasPreviousEOISubmission = eoiSurveyData?.QualificationSurveySectionResponse.isComplete;

  // Check if user has any previous EOI engagement (survey submission or completion)
  const shouldBypassEOI = hasPreviousEOISubmission || hasCompletedEoi;

  const applicationUrlExists =
    !!pageContent?.expressionOfInterest?.applyButtonUrl || !!pageContent?.expressionOfInterest?.hostedApplicationUrl;

  // Allow direct application access when user has EOI history and application URL exists
  const shouldBypassEOIModal = shouldBypassEOI && applicationUrlExists;

  // Qualifying Questions
  const [isQualified, setIsQualified] = useState<boolean>(false);
  const [hasCompletedQQForm, setHasCompletedQQForm] = useState<boolean>(false);
  const [loadingQQUserData, setLoadingQQUserData] = useState<boolean>(true);

  const [highestEducation, setHighestEducation] = useState<number | null>();
  const [learnerTimeline, setLearnerTimeline] = useState<number | null>();
  const [financePlan, setFinancePlan] = useState<number | null>();

  const [initialSection, setInitialSection] = useState<SectionName>();

  const { slug } = useParams();
  // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const QQExperimentVariant = getQualifyingQuestionsExpt(slug, true);
  const isLoggedIn = isAuthenticatedUser();

  // Required EOI is for Destination Degrees only
  const requiredEoiEnabled = isDestinationDegree(slug || '');

  useEffect(() => {
    const fetchUserInput = async () => {
      if (isLoggedIn && QQExperimentVariant !== 'control') {
        const userInputResponse = await getInputfromUserProfile();
        const { qualifyingQuestions } = userInputResponse;
        const {
          highestEducation: profileHighestEducation,
          learnerTimeline: profileLearnerTimeline,
          financePlan: profileFinancePlan,
        } = qualifyingQuestions || {};

        setHighestEducation(profileHighestEducation);
        setLearnerTimeline(profileLearnerTimeline);
        setFinancePlan(profileFinancePlan);

        if (profileHighestEducation && profileLearnerTimeline && profileFinancePlan) {
          setHasCompletedQQForm(true);
        }
        const qualificationFromProfile = isLearnerQualified(profileHighestEducation, productVariant);
        setIsQualified(qualificationFromProfile);
        setLoadingQQUserData(false);
      } else {
        setHasCompletedQQForm(false);
        setIsQualified(false);
        setLoadingQQUserData(false);
      }
    };
    fetchUserInput();
  }, [isLoggedIn, QQExperimentVariant, productVariant]);

  const eoiButtonsToUse = useMemo(() => {
    if (requiredEoiEnabled) {
      return [ACTION_TYPES.emailMeInfo];
    }
    if (shouldBypassEOIModal) {
      if (QQExperimentVariant === 'B' && !loadingQQUserData) {
        if (hasCompletedQQForm) {
          return isQualified ? [ACTION_TYPES.apply] : [ACTION_TYPES.apply, ACTION_TYPES.nextSteps];
        } else {
          return [ACTION_TYPES.apply, ACTION_TYPES.qualifyingQuestions];
        }
      }
      return [ACTION_TYPES.apply];
    }
    return [ACTION_TYPES.apply, ACTION_TYPES.emailMeInfo];
  }, [
    requiredEoiEnabled,
    shouldBypassEOIModal,
    QQExperimentVariant,
    loadingQQUserData,
    hasCompletedQQForm,
    isQualified,
  ]);

  useEffect(() => {
    if (QQExperimentVariant === 'B' && shouldBypassEOIModal) {
      if (hasCompletedQQForm && !isQualified) {
        setInitialSection('NextSteps');
      } else if (!hasCompletedQQForm) {
        setInitialSection('QualifyingQuestions');
      }
    }
  }, [QQExperimentVariant, shouldBypassEOIModal, hasCompletedQQForm, isQualified]);

  return (
    <DegreeDescriptionEoiStateContext.Provider
      value={{
        hasCompletedEoi,
        setHasCompletedEoi,
        shouldBypassEOIModal,
        isQualified,
        setIsQualified,
        hasCompletedQQForm,
        setHasCompletedQQForm,
        loadingQQUserData,
        setLoadingQQUserData,
        highestEducation,
        setHighestEducation,
        learnerTimeline,
        setLearnerTimeline,
        financePlan,
        setFinancePlan,
        eoiButtonsToUse,
        initialSection,
        setInitialSection,
      }}
    >
      {children}
    </DegreeDescriptionEoiStateContext.Provider>
  );
};
